import { ArrowLeft, Headset, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { SupportEpisode, SupportEvent, SupportHandoff, SupportMessage, StaffMe } from '../api/types';
import { ago } from '../lib/format';
import { can } from '../lib/me';
import { lastMessageId, mergeSupportMessages, outcome } from '../lib/support';

/**
 * One operator's help conversation, answered from here.
 *
 * The conversation lives in sphere-crm and is read through its support door;
 * this screen polls the tail of it every few seconds while it is open, which is
 * the same posture as the operator's own panel on the other end. What staff
 * type is written into the operator's thread by the CRM — signed "LimoSphere
 * Support", never with a name or an email, whoever is typing.
 *
 * Only the person who claimed a request writes into it. A colleague who needs
 * to step in takes it over explicitly; the one who had it can no longer write,
 * which is what stops two people answering one operator at once.
 */
const POLL_MS = 3000;
// An operator panel polls every few seconds while it is open. Longer than this
// without a poll means it probably is not open.
const PANEL_QUIET_MS = 60_000;

const WHO: Record<SupportMessage['role'], string> = {
  user: 'Operator',
  assistant: 'Assistant',
  staff: 'LimoSphere Support',
  system: '',
};

function Transcript({
  messages,
  operatorName,
  authors,
}: {
  messages: SupportMessage[];
  operatorName: string | null;
  authors: Map<string, string>;
}) {
  const listRef = useRef<HTMLDivElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs when a message arrives, to keep the newest in view; the list is read through its ref
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  return (
    <div className="support-thread" ref={listRef}>
      {messages.map((message) => {
        if (message.role === 'system') {
          return (
            <p key={message.id} className="support-notice">
              {message.content}
            </p>
          );
        }
        // Which colleague wrote a reply comes from this console's own trail —
        // the operator was only ever shown the desk's name.
        const wrote = message.role === 'staff' ? authors.get(message.id) : null;
        return (
          <div key={message.id} className={`support-msg is-${message.role}`}>
            <span className="support-msg-who">
              {message.role === 'user' ? operatorName || WHO.user : WHO[message.role]}
              {wrote ? ` · ${wrote}` : ''}
              <span className="assist-muted"> · {ago(message.createdAt)}</span>
            </span>
            <div className="support-bubble">
              {/* The assistant writes markdown for the operator's panel; here it
                  is context, so its emphasis markers are dropped, not rendered. */}
              {message.role === 'assistant' ? message.content.replace(/\*\*(.+?)\*\*/g, '$1') : message.content}
            </div>
            {message.attachments.length > 0 && (
              <div className="support-shots">
                {message.attachments.map((shot) =>
                  shot.url ? (
                    <a key={shot.key} href={shot.url} target="_blank" rel="noopener noreferrer" title={shot.fileName}>
                      <img src={shot.url} alt={shot.fileName} />
                    </a>
                  ) : (
                    <span key={shot.key} className="assist-muted">
                      {shot.fileName}
                    </span>
                  ),
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SupportConversation({ id, me, onBack }: { id: string; me: StaffMe | null; onBack: () => void }) {
  const [handoff, setHandoff] = useState<SupportHandoff | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [events, setEvents] = useState<SupportEvent[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // A reply half-written survives leaving the conversation or reloading the
  // page -- per conversation, in this tab only, and gone once it is sent.
  const draftKey = `watch.support.draft.${id}`;
  const [draft, setDraft] = useState(() => {
    try {
      return sessionStorage.getItem(draftKey) ?? '';
    } catch {
      return '';
    }
  });
  useEffect(() => {
    try {
      if (draft) sessionStorage.setItem(draftKey, draft);
      else sessionStorage.removeItem(draftKey);
    } catch {
      // Blocked storage: the draft lives as long as this screen, as before.
    }
  }, [draftKey, draft]);
  const [sending, setSending] = useState(false);
  // One id per SUBMISSION: a retry of the same text after a lost response
  // reuses it, so the CRM writes it once; editing the text makes it a new one.
  const submissionId = useRef<string | null>(null);
  const messagesRef = useRef<SupportMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  /** The whole episode — the first read is what records that it was opened. */
  const load = async () => {
    const episode = await api.get<SupportEpisode>(`/support/${id}`);
    setHandoff(episode.handoff);
    setMessages((current) => mergeSupportMessages(current, episode.messages));
    setEvents(episode.events || []);
    setTruncated(!!episode.truncated);
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get<SupportEpisode>(`/support/${id}`)
      .then((episode) => {
        if (cancelled) return;
        setHandoff(episode.handoff);
        setMessages(episode.messages);
        setEvents(episode.events || []);
        setTruncated(!!episode.truncated);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const open = handoff?.status === 'waiting' || handoff?.status === 'with_staff';
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let inFlight = false;
    const timer = setInterval(async () => {
      // Never stack polls on a slow CRM — the next tick covers this one.
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await api.get<{ handoff: SupportHandoff; messages: SupportMessage[] }>(
          `/support/${id}/messages?after=${lastMessageId(messagesRef.current)}`,
        );
        if (cancelled) return;
        if (res.messages.length) setMessages((current) => mergeSupportMessages(current, res.messages));
        setHandoff(res.handoff);
      } catch {
        // A dropped poll is retried on the next tick; the banner is for actions.
      } finally {
        inFlight = false;
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id, open]);

  const canAct = can(me, 'support.respond');
  const mine = handoff?.status === 'with_staff' && !!me && handoff.claimedBy === me.email;

  const act = async (name: string, path: string, body?: unknown, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(name);
    setActionError(null);
    try {
      const res = await api.post<SupportEpisode>(`/support/${id}/${path}`, body);
      if (res.handoff) setHandoff(res.handoff);
      if (res.messages) setMessages((current) => mergeSupportMessages(current, res.messages));
      await load().catch(() => undefined);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
      await load().catch(() => undefined);
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    if (!submissionId.current) submissionId.current = crypto.randomUUID();
    setSending(true);
    setActionError(null);
    try {
      const res = await api.post<{ message: SupportMessage; handoff: SupportHandoff }>(`/support/${id}/messages`, {
        content,
        clientMsgId: submissionId.current,
      });
      setMessages((current) => mergeSupportMessages(current, [res.message]));
      setHandoff(res.handoff);
      setDraft('');
      submissionId.current = null;
      await load().catch(() => undefined);
    } catch (caught) {
      // The text stays in the box, and the same submission id stays with it.
      setActionError(`Not sent — ${caught instanceof Error ? caught.message : String(caught)}`);
    } finally {
      setSending(false);
    }
  };

  const authors = new Map(
    events
      .filter((event) => event.kind === 'replied' && event.payload && typeof event.payload.messageId === 'string')
      .map((event) => [String(event.payload?.messageId), event.actor] as [string, string]),
  );

  if (loadError && !handoff) {
    return (
      <div className="page">
        <button type="button" className="btn btn-ghost support-back" onClick={onBack}>
          <ArrowLeft size={14} /> Back to the queue
        </button>
        <p className="note note-danger">Could not open this conversation: {loadError}</p>
      </div>
    );
  }
  if (!handoff) return <div className="page note">Loading…</div>;

  const seenAt = handoff.operatorSeenAt ? new Date(handoff.operatorSeenAt).getTime() : null;
  const panelQuiet = open && (!seenAt || Date.now() - seenAt > PANEL_QUIET_MS);
  const page = handoff.page;

  return (
    <div className="page support-page">
      <button type="button" className="btn btn-ghost support-back" onClick={onBack}>
        <ArrowLeft size={14} /> Back to the queue
      </button>

      <div className="card card-pad support-head">
        <div className="assist-card-head">
          <span className="badge badge-accent">{handoff.tenantKey}</span>
          <span className={`badge ${handoff.status === 'waiting' ? 'badge-warn' : handoff.status === 'with_staff' ? 'badge-ok' : ''}`}>
            {handoff.status === 'with_staff' ? 'with staff' : handoff.status}
          </span>
          {handoff.urgency === 'urgent' && <span className="badge badge-danger">urgent</span>}
          <span className="assist-muted">asked {ago(handoff.waitingSince)}</span>
          <span className="wt-spacer" />
          {canAct && handoff.status === 'waiting' && (
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => void act('claim', 'claim')}>
              {busy === 'claim' ? 'Claiming…' : 'Claim'}
            </button>
          )}
          {canAct && handoff.status === 'with_staff' && !mine && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!!busy}
              onClick={() =>
                void act(
                  'takeover',
                  'claim',
                  { takeover: true },
                  `Take this conversation over from ${handoff.claimedBy}? They will no longer be able to reply.`,
                )
              }
            >
              Take over
            </button>
          )}
          {mine && (
            <>
              <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => void act('release', 'release')}>
                Put back on the queue
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!!busy}
                onClick={() =>
                  void act(
                    'close',
                    'close',
                    undefined,
                    'Close this conversation? The assistant answers the operator from here, and they can ask for a person again.',
                  )
                }
              >
                Close conversation
              </button>
            </>
          )}
        </div>
        <p className="assist-who">
          {handoff.operator.name || 'An operator'}
          {handoff.operator.role && <span className="assist-muted"> · {handoff.operator.role}</span>}
          {handoff.operator.email && (
            <span className="assist-muted">
              {' '}
              · <a href={`mailto:${handoff.operator.email}`}>{handoff.operator.email}</a>
            </span>
          )}
          {handoff.screen && <span className="assist-muted"> · on {handoff.screen}</span>}
        </p>
        {handoff.topic && <p className="assist-note">“{handoff.topic}”</p>}
        {page?.state && Object.keys(page.state).length > 0 && (
          <dl className="assist-context">
            {page.title && (
              <div>
                <dt>screen</dt>
                <dd>{page.title}</dd>
              </div>
            )}
            {Object.entries(page.state).map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
          </dl>
        )}
        <p className="support-status-line">
          {handoff.status === 'with_staff' && !mine && `With ${handoff.claimedBy}.`}
          {mine && 'You have this conversation. The operator sees “LimoSphere Support”, not your name.'}
          {handoff.status === 'waiting' && 'Nobody has this yet.'}
          {!open && `${outcome(handoff)}${handoff.closedAt ? ` ${ago(handoff.closedAt)}` : ''}.`}
          {panelQuiet && " The operator's help panel has not checked in for a while — they may have closed it."}
        </p>
      </div>

      {actionError && <p className="note note-danger">{actionError}</p>}

      <div className="card support-convo">
        {truncated && <p className="support-notice">Earlier messages are not shown — only the last hundred.</p>}
        <Transcript messages={messages} operatorName={handoff.operator.name} authors={authors} />
        {mine ? (
          <div className="support-composer">
            <textarea
              className="textarea"
              placeholder="Reply to the operator…"
              value={draft}
              maxLength={4000}
              onChange={(e) => {
                setDraft(e.target.value);
                // Different words are a different submission.
                submissionId.current = null;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button type="button" className="btn btn-primary" disabled={sending || !draft.trim()} onClick={() => void send()}>
              <Send size={14} /> {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        ) : (
          open && (
            <p className="support-composer-off">
              <Headset size={14} />
              {canAct ? 'Claim this conversation to reply.' : 'Replying needs the responder role.'}
            </p>
          )
        )}
      </div>

      {events.length > 0 && (
        <>
          <h2 className="assist-subhead">History</h2>
          <ul className="support-events">
            {events.map((event) => (
              <li key={event.id}>
                <span className="assist-muted">{ago(event.createdAt)}</span> {event.actor}{' '}
                {event.kind.replace('_', ' ')}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
